import PropTypes from 'prop-types';
import { useState } from 'react';
import {
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Box,
  Alert,
  CircularProgress,
} from '@mui/material';
import { sentenceCase } from 'change-case';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import TableCell from '@mui/material/TableCell';
import call from '../../../core/services/http';
import Label from '../../../components/label';
import { getAuditRecords } from '../../../packages/audit/store/auditActions';
import { fDate } from '../../../utils/formatTime';

// ----------------------------------------------------------------------

export default function AuditTableRow({ selected, row, handleSelectClick, onRefresh }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      const response = await call('delete', `audits/${row.id}/`);
      if (response.data.status) {
        setDeleteDialogOpen(false);
        // Refresh the audit records list using parent callback or fallback to dispatch
        if (onRefresh) {
          onRefresh();
        } else {
          dispatch(getAuditRecords({ page: 1, rowsPerPage: 100 }));
        }
      }
    } catch (error) {
      console.error('Delete error:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };
  return (
    <>
      <TableRow hover tabIndex={-1} role="checkbox" selected={selected}>
        <TableCell padding="checkbox">
          <Checkbox disableRipple checked={selected} onChange={handleSelectClick} />
        </TableCell>

        <TableCell align="left">{row?.name}</TableCell>
        <TableCell align="left">
          {row?.total_scanned}/{row?.total_assets}
        </TableCell>
        <TableCell align="left">{fDate(row?.start_date)}</TableCell>
        <TableCell align="left">{fDate(row?.end_date)}</TableCell>
        <TableCell align="left">{row?.location?.name}</TableCell>
        <TableCell align="left">{row?.auditor?.display_name}</TableCell>
        <TableCell align="left">{fDate(row?.completed_date)}</TableCell>
        <TableCell align="left">{row?.reconciler?.display_name}</TableCell>

        <TableCell align="left">
          <Label color={(row?.status === 'pending' && 'error') || 'success'}>{sentenceCase(row?.status)}</Label>
        </TableCell>
        <TableCell>
          <Stack direction="row" spacing={1}>
            <Button size="small" variant="contained" onClick={() => navigate(`/dashboard/audits/${row?.id}`)}>
              View
            </Button>
            {row?.status !== 'completed' && (
              <Button size="small" variant="contained" color="error" onClick={handleDeleteClick} disabled={isDeleting}>
                Delete
              </Button>
            )}
          </Stack>
        </TableCell>
      </TableRow>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Typography variant="h5" component="div" color="error.main" fontWeight="bold">
            Delete Audit Record
          </Typography>
        </DialogTitle>

        <DialogContent>
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This action cannot be undone. All associated audit data will be permanently
              deleted.
            </Typography>
          </Alert>

          <DialogContentText sx={{ mb: 2, fontSize: '1rem' }}>
            Are you sure you want to delete the audit record:
          </DialogContentText>

          <Box
            sx={{
              p: 2,
              backgroundColor: 'grey.50',
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'grey.200',
              mb: 2,
            }}
          >
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              {row?.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Location:</strong> {row?.location?.name || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Auditor:</strong> {row?.auditor?.display_name || 'N/A'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Status:</strong> {sentenceCase(row?.status || 'unknown')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Total Assets:</strong> {row?.total_assets || 0}
            </Typography>
          </Box>

          <DialogContentText color="error.main" fontWeight="medium">
            This will permanently delete all audit data, including scanned items and audit results.
          </DialogContentText>
        </DialogContent>

        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button onClick={handleDeleteCancel} variant="outlined" disabled={isDeleting} sx={{ borderRadius: 2, px: 3 }}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            color="error"
            disabled={isDeleting}
            startIcon={isDeleting ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{
              borderRadius: 2,
              px: 3,
              minWidth: 120,
            }}
          >
            {isDeleting ? 'Deleting...' : 'Delete Audit'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

AuditTableRow.propTypes = {
  row: PropTypes.object,
  handleSelectClick: PropTypes.func,
  selected: PropTypes.any,
  onRefresh: PropTypes.func,
};
